import { Eye, EyeOff, Map, MapPin, PenTool, Plus, Minus, GripVertical, X, Ruler, Flame, Search, Wand2, Loader2 } from 'lucide-react';

// Strip stray "None" tokens (e.g. make/model both null → "None None")
const cleanLabel = (s) => {
  if (!s) return '';
  return String(s).replace(/\bnone\b/gi, '').replace(/\s+/g, ' ').trim();
};

// Pin colors by discipline (match FloorPlanCanvas)
const DISC_PIN_HEX = {
  M: '#0EA5E9', E: '#F59E0B', P: '#10B981', FP: '#EF4444',
  S: '#8B5CF6', A: '#64748B', C: '#A16207', L: '#84CC16', G: '#6366F1',
};
const pinHex = (d) => DISC_PIN_HEX[String(d || '').toUpperCase()] || '#8B5CF6';

const DISC_COLORS = {
  mechanical: { text: 'text-red-500' },
  hvac: { text: 'text-red-500' },
  electrical: { text: 'text-yellow-500' },
  plumbing: { text: 'text-green-500' },
  'fire protection': { text: 'text-orange-500' },
  architectural: { text: 'text-blue-500' },
  structural: { text: 'text-purple-500' },
};

export default function FloorPlanLayerPanel({
  baseLayers, setBaseLayers, layerData,
  userAnnotations, strokes,
  overlaySheets, overlayVisibility, setOverlayVisibility,
  removeOverlay, onAddOverlay,
  unmatched, draggingAsset, setDraggingAsset,
  pinnedAssets, unpinAsset, onLocate, pinQuery, setPinQuery, onSmartLocate, onSmartLocateAll, locating,
  hoveredAsset, setHoveredAsset,
  showHeatmap, setShowHeatmap,
}) {
  const pinQ = (pinQuery || '').trim().toLowerCase();
  const pinsFiltered = pinQ
    ? (pinnedAssets || []).filter(pa => `${pa.tag || ''} ${cleanLabel(pa.name)} ${pa.discipline || ''}`.toLowerCase().includes(pinQ))
    : (pinnedAssets || []);
  const layers = [
    { key: 'rooms', label: 'Rooms', icon: Map, color: 'text-blue-500', count: (layerData.rooms || []).length },
    { key: 'assets', label: 'Auto-Matched', icon: MapPin, color: 'text-amber-500', count: (layerData.assets || []).length },
    { key: 'pinnedAssets', label: 'Pinned Assets', icon: MapPin, color: 'text-purple-500', count: (pinnedAssets || []).length },
    { key: 'annotations', label: 'Annotations', icon: PenTool, color: 'text-emerald-500', count: userAnnotations.length + strokes.length },
    { key: 'labels', label: 'Dimension Labels', icon: Ruler, color: 'text-cyan-500', count: null },
    { key: 'heatmap', label: 'Takeoff Heat Map', icon: Flame, color: 'text-orange-500', count: null },
  ];

  return (
    <div className="w-60 border-l border-border bg-card shrink-0 overflow-y-auto" data-testid="fp-layer-panel">
      {/* Base layers */}
      <div className="p-3 border-b border-border">
        <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Base Layers</p>
      </div>
      {layers.map(l => (
        <button key={l.key} onClick={() => {
            if (l.key === 'heatmap') {
              setShowHeatmap(prev => !prev);
            } else {
              setBaseLayers(prev => ({ ...prev, [l.key]: !prev[l.key] }));
            }
          }}
          data-testid={`fp-layer-${l.key}`}
          className={`flex items-center gap-3 w-full px-3 py-2 text-left border-b border-border/50 transition-colors ${
            (l.key === 'heatmap' ? showHeatmap : baseLayers[l.key]) ? 'hover:bg-muted/50' : 'opacity-50 hover:opacity-70'
          }`}>
          {(l.key === 'heatmap' ? showHeatmap : baseLayers[l.key]) ? <Eye className={`w-3.5 h-3.5 ${l.color}`} /> : <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-heading text-foreground">{l.label}</p>
            {l.count !== null && <p className="text-[9px] text-muted-foreground">{l.count} items</p>}
            {l.count === null && <p className="text-[9px] text-muted-foreground">toggle</p>}
          </div>
        </button>
      ))}

      {/* Overlay sheets */}
      {(overlaySheets || []).length > 0 && (
        <>
          <div className="p-3 border-b border-border">
            <p className="text-[9px] font-heading uppercase tracking-widest text-muted-foreground">Discipline Overlays</p>
          </div>
          {overlaySheets.map(os => {
            const colors = DISC_COLORS[(os.discipline || '').toLowerCase()] || {};
            return (
              <div key={os.id} className="flex items-center gap-2 px-3 py-2 border-b border-border/50">
                <button onClick={() => setOverlayVisibility(prev => ({ ...prev, [os.id]: !prev[os.id] }))}
                  data-testid={`fp-overlay-toggle-${os.id}`} className="shrink-0">
                  {overlayVisibility[os.id]
                    ? <Eye className={`w-3.5 h-3.5 ${colors.text || 'text-muted-foreground'}`} />
                    : <EyeOff className="w-3.5 h-3.5 text-muted-foreground" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-heading text-foreground truncate">{os.sheet_name || os.sheet_number || 'Sheet'}</p>
                  <p className="text-[9px] text-muted-foreground truncate">{[os.sheet_number, os.discipline].filter(Boolean).join(' · ')}</p>
                </div>
                <button onClick={() => removeOverlay(os.id)} className="text-muted-foreground hover:text-destructive shrink-0"
                  data-testid={`fp-overlay-remove-${os.id}`}>
                  <X className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </>
      )}

      <button onClick={onAddOverlay} data-testid="fp-add-overlay-btn"
        className="flex items-center gap-2 w-full px-3 py-2.5 text-[10px] font-heading text-primary hover:bg-primary/5 transition-colors border-b border-border/50">
        <Plus className="w-3 h-3" /> Add Discipline Overlay
      </button>

      {/* Unmatched assets */}
      {unmatched.length > 0 && (
        <>
          <div className="p-3 border-b border-border">
            <p className="text-[9px] font-heading uppercase tracking-widest text-rose-500">Unmatched Assets</p>
            <p className="text-[8px] text-muted-foreground mt-0.5">Click to pin on plan</p>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {unmatched.map((ua, i) => (
              <div key={ua.asset_id}
                className={`flex items-center gap-2 px-3 py-2 border-b border-border/30 cursor-grab hover:bg-muted/50 transition-colors ${
                  draggingAsset?.asset_id === ua.asset_id ? 'bg-amber-50 dark:bg-amber-950/20' : ''
                }`}
                data-testid={`fp-unmatched-${i}`}
                onClick={() => setDraggingAsset(ua)}>
                <GripVertical className="w-3 h-3 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-heading text-foreground truncate">{ua.tag || cleanLabel(ua.name) || 'Equipment'}</p>
                  {cleanLabel(ua.name) && <p className="text-[9px] text-muted-foreground truncate">{cleanLabel(ua.name)}</p>}
                </div>
                <MapPin className="w-3 h-3 text-rose-400 shrink-0" />
              </div>
            ))}
          </div>
        </>
      )}

      {/* Pinned assets */}
      {(pinnedAssets || []).length > 0 && (
        <>
          <div className="p-3 border-b border-border">
            <p className="text-[9px] font-heading uppercase tracking-widest text-purple-500">
              Pinned Assets ({pinQ ? `${pinsFiltered.length} of ${pinnedAssets.length}` : pinnedAssets.length})
            </p>
            <p className="text-[8px] text-muted-foreground mt-0.5 mb-2">Click to jump to its pin</p>
            {onSmartLocate && (
              <button onClick={onSmartLocate} disabled={locating} data-testid="fp-smart-locate-btn"
                title="Use AI to move these pins onto the real equipment on this sheet"
                className="flex items-center justify-center gap-1.5 w-full mb-2 px-2 py-1.5 text-[10px] font-heading uppercase tracking-wider rounded-sm border border-sky-400/40 text-sky-600 dark:text-sky-400 hover:bg-sky-500/10 transition-colors disabled:opacity-50">
                {locating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                {locating ? 'Locating…' : 'AI Smart Locate'}
              </button>
            )}
            {onSmartLocateAll && (
              <button onClick={onSmartLocateAll} disabled={locating} data-testid="fp-smart-locate-all-btn"
                title="Run discipline-aware Smart Locate on every sheet — places each discipline's pins on its own plan"
                className="flex items-center justify-center gap-1.5 w-full mb-2 px-2 py-1.5 text-[10px] font-heading uppercase tracking-wider rounded-sm border border-violet-400/40 text-violet-600 dark:text-violet-400 hover:bg-violet-500/10 transition-colors disabled:opacity-50">
                {locating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                {locating ? 'Locating…' : 'Locate All Disciplines'}
              </button>
            )}
            <div className="relative">
              <Search className="w-3 h-3 text-muted-foreground absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                value={pinQuery}
                onChange={(e) => setPinQuery(e.target.value)}
                placeholder="Search tag or equipment…"
                data-testid="fp-pin-search"
                className="w-full pl-7 pr-2 py-1.5 text-[10px] rounded-sm bg-muted/40 border border-border focus:border-primary focus:outline-none"
              />
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {(() => {
              const list = pinsFiltered;
              if (list.length === 0) {
                return <p className="px-3 py-3 text-[10px] text-muted-foreground" data-testid="fp-pin-search-empty">No matching pins</p>;
              }
              return list.map((pa) => (
                <div key={pa.asset_id}
                  className="flex items-center gap-2 px-3 py-2 border-b border-border/30 cursor-pointer hover:bg-muted/50 transition-colors"
                  data-testid={`fp-pinned-${pa.asset_id}`}
                  onClick={() => onLocate?.(pa)}>
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: pinHex(pa.discipline) }}
                    title={pa.discipline || ''} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-heading text-foreground truncate">{pa.tag || cleanLabel(pa.name) || 'Equipment'}</p>
                    {cleanLabel(pa.name) && <p className="text-[9px] text-muted-foreground truncate">{cleanLabel(pa.name)}</p>}
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); unpinAsset(pa.asset_id); }} className="text-muted-foreground hover:text-destructive shrink-0"
                    data-testid={`fp-unpin-${pa.asset_id}`}>
                    <Minus className="w-3 h-3" />
                  </button>
                </div>
              ));
            })()}
          </div>
        </>
      )}

      {/* Auto-matched assets */}
      {baseLayers.assets && (layerData.assets || []).length > 0 && (
        <>
          <div className="p-3 border-b border-border">
            <p className="text-[9px] font-heading uppercase tracking-widest text-amber-500">Auto-Matched Assets</p>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {(layerData.assets || []).map((m, i) => (
              <div key={i} className="px-3 py-1.5 hover:bg-muted/50 cursor-pointer text-[10px] border-b border-border/30"
                onMouseEnter={() => setHoveredAsset(m)} onMouseLeave={() => setHoveredAsset(null)}>
                <p className="font-heading text-foreground truncate">{m.tag || 'Equipment'}</p>
                <p className="font-mono text-muted-foreground truncate">{m.name}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
